import AppTheme from '@libs/mui/AppTheme';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { ImperativeDialog } from '@shared/components/dialogs/imperative-dialog';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import { Box, Button, Chip, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useRef } from 'react';
import HelpIcon from '@mui/icons-material/Help';

type OnboardingStepCardProps = {
  index: number;
  title: string;
  description: string;
  ctaLabel: string;
  isDone: boolean;
  doneLabel: string;
  todoLabel: string;
  onClick: () => void;
  detailContent?: React.ReactNode;
  requiredSubscription?: 'expert' | 'pro';
  isSubscriptionLocked?: boolean;
  openDetailOnClick?: boolean;
};

const doneColor = AppTheme.palette.success.main;
const todoColor = AppTheme.palette.error.main;

const OnboardingStepCard = (props: OnboardingStepCardProps) => {
  const {
    index,
    title,
    description,
    detailContent,
    ctaLabel,
    doneLabel,
    isDone,
    todoLabel,
    onClick,
    requiredSubscription,
    isSubscriptionLocked = false,
    openDetailOnClick = false,
  } = props;
  const dialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const subscriptionLabel = requiredSubscription
    ? `Vyžaduje ${requiredSubscription.toUpperCase()}`
    : undefined;

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dialogRef.current?.open();
  };

  const handleInfoMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Paper
      sx={{
        position: 'relative',
        height: '100%',
        boxSizing: 'border-box',
        p: 0,
        borderRadius: 2,
        border: `1px solid ${isDone ? doneColor : todoColor}`,
        backgroundColor: '#ffffff',
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          borderRadius: '0% 0% 10px 0% ',
          textAlign: 'center',
          width: '20px',
          height: '20px',
          left: -2,
          top: -2,
          backgroundColor: isDone ? doneColor : todoColor,
          color: AppTheme.palette.common.white,
          display: 'flex',
          alignItems: 'center',
          lineHeight: 0,
          justifyContent: 'center',
        }}
      >
        <Typography variant="body1" fontSize="10px">
          {`${index}.`}
        </Typography>
      </Box>

      <Stack direction="column" spacing={1} sx={{ p: 1.25, pt: 3 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ flexGrow: 1 }}>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.5}>
              <Typography
                variant="caption"
                sx={{
                  color: isDone ? doneColor : todoColor,
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                {isDone ? doneLabel : todoLabel}
              </Typography>
              {isSubscriptionLocked && subscriptionLabel && (
                <Chip
                  icon={<LockRoundedIcon sx={{ fontSize: 12 }} />}
                  label={subscriptionLabel}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'warning.dark',
                    backgroundColor: 'warning.light',
                  }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.primary" fontWeight={700} lineHeight={1.3}>
              {title}
            </Typography>
            <Stack direction="row" spacing={0.25} alignItems="center">
              <Typography
                variant="caption"
                color="text.secondary"
                fontSize={11.5}
                lineHeight={1.35}
                sx={{ flexGrow: 1 }}
              >
                {description}
              </Typography>
              {detailContent && (
                <IconButton
                  size="small"
                  sx={{ p: 0.25 }}
                  onClick={handleInfoClick}
                  onMouseDown={handleInfoMouseDown}
                  onPointerDown={handleInfoMouseDown}
                >
                  <HelpIcon sx={{ fontSize: 18, color: 'info.main' }} />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          onClick={!isSubscriptionLocked ? onClick : undefined}
          sx={{ cursor: isSubscriptionLocked ? 'not-allowed' : 'pointer' }}
        >
          {/* {isDone ? (
            <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: 16 }} />
          ) : (
            <ArrowForwardIcon sx={{ color: 'error.main', fontSize: 16 }} />
          )}
          <Typography
            variant="caption"
            sx={{ color: isDone ? 'success.main' : 'error.main', fontWeight: 700 }}
          >
            {ctaLabel}
          </Typography> */}

          <Button
            disabled={isSubscriptionLocked}
            onClick={openDetailOnClick ? () => dialogRef.current?.open() : undefined}
            startIcon={
              isDone ? (
                <CheckCircleRoundedIcon sx={{ color: doneColor, fontSize: 16 }} />
              ) : (
                <ArrowForwardIcon sx={{ color: todoColor, fontSize: 16 }} />
              )
            }
            variant="contained"
            sx={{
              backgroundColor: isDone ? doneColor : todoColor,
              color: 'common.white',
              textTransform: 'none',
              fontSize: 12,
              '&:hover': {
                backgroundColor: isDone ? 'success.dark' : 'error.dark',
              },
              '&.Mui-disabled': {
                backgroundColor: 'grey.400',
                color: 'common.white',
              },
            }}
          >
            {isSubscriptionLocked && subscriptionLabel ? subscriptionLabel : ctaLabel}
          </Button>
        </Stack>
      </Stack>

      <ImperativeDialog ref={dialogRef} title={title} content={detailContent} />
    </Paper>
  );
};

export default OnboardingStepCard;
